import { JsonRpcSigner, Signer } from 'ethers';
import { useEffect, useRef, useState } from 'react';

import { getMainPrivateApi } from 'api/apiCoreSdk';
import { ProvidersMap } from 'components/wallet/unlock/discovery/walletDiscoveryEip6963';
import { walletsList } from 'components/wallet/unlock/walletsListCore';
import {
  getGlobalEnforcedChain,
  throwOnDifferentThanCurrentChainId,
} from 'lib/ethereum/chains';
import { UnreachableCaseError } from 'lib/utils';
import { DelegatedKeyAuthorization } from 'reduxState/delegatedKey/authorizationReducer';
import { staticDispatch, useSelector } from 'reduxState/store';
import {
  IWallet,
  setPersonalWalletSecret,
} from 'reduxState/wallet/walletReducer';
import { WalletType } from 'reduxState/wallet/walletUtils';

import { getDiscoveredWalletSigner } from '../walletDiscovery/walletDiscovery';

import {
  disconnectCoinbaseWallet,
  getSigner as getCoinbaseWalletSigner,
  isCoinbaseApp,
} from './coinbaseWallet';
import { getDelegatedKeySigner } from './delegatedKeyWallet';
import { getSigner as getPrivateKeySigner } from './privateKeyWallet';
import {
  getSoftwareWalletAccount,
  getSigner as getSotwareWalletSigner,
} from './softwareWallet';
import {
  getSigner as getWalletConnectSigner,
  removeWalletConnectFromLocalStorage,
} from './walletConnectV2';
// import { getSigner as getLedgerSigner } from './hwLedger';
// import { getSigner as getTrezorSigner } from './hwTrezor';

const throwOnIncorrectChain = async (signer: Signer) => {
  const providerNetwork = await signer.provider?.getNetwork();
  const walletChainId = providerNetwork?.chainId;
  if (!walletChainId) {
    throw new Error(
      'Cannot detect chain you wallet is running on. If you think this is a bug, please contact support'
    );
  }
  throwOnDifferentThanCurrentChainId(walletChainId);
};

const throwOnAddressMissmatch = async (
  wallet: IWallet,
  signer: Signer,
  detectedWallet?: string
) => {
  const address = await signer.getAddress();
  if (
    address.toLowerCase() !== wallet.walletAddress.toLowerCase() ||
    (detectedWallet && address.toLowerCase() !== detectedWallet?.toLowerCase())
  ) {
    throw new Error(
      `Wallet address mismatch. Are you using a different wallet than one you unlocked?`
    );
  }
};

let fetchingGuard: Promise<{ personalWalletSecret: string }> | undefined;

/**
 * TODO: Security - Store PersonalSecret outside of Redux (Local variable)
 * Gets personal wallet secret if it exists already, otherwise requests it from
 * the server and returns that, saving it to state as well.
 * @param wallet wallet or delegatedKey to get the personalSecret for
 */
async function getPersonalWalletSecret(wallet: IWallet): Promise<string> {
  // When already initialized - available until page refresh (Stored only in RAM, not Local storage)
  const { personalWalletSecret } = wallet;
  if (personalWalletSecret) {
    return personalWalletSecret;
  }
  if (!fetchingGuard) {
    fetchingGuard = getMainPrivateApi().getWalletGuard(wallet.walletAddress);
  }
  // Secret not initialized
  const response = await fetchingGuard;
  staticDispatch(setPersonalWalletSecret(response.personalWalletSecret));
  fetchingGuard = undefined;
  return response.personalWalletSecret;
}

/**
 * Used to pre-fetch personal secret in BuySell panel
 * to speed up order placing later.
 * Only fetch once user modify input.
 */
export function usePreFetchPersonalWalletSecret(watchValue: string) {
  const wallet = useSelector(state => state.wallet);
  const fetchedOnce = useRef(false);
  const [initialValue] = useState(watchValue);
  useEffect(() => {
    if (fetchedOnce.current || initialValue === watchValue) {
      return;
    }
    fetchedOnce.current = true;
    getPersonalWalletSecret(wallet);
  }, [initialValue, wallet, watchValue]);
}

/**
 * Get Signer without checking if it's running on current network
 * eg. Useful to call before triggering chain change.
 */
export const getUncheckedSigner = (
  custodyWallet: IWallet,
  delegatedKeyAuthorization?: DelegatedKeyAuthorization
) => getSigner(custodyWallet, delegatedKeyAuthorization, false);

export const getSigner = async (
  custodyWallet: IWallet,
  delegatedKeyAuthorization?: DelegatedKeyAuthorization,
  networkChainCheck = true
): Promise<JsonRpcSigner | Signer> => {
  const wallet = delegatedKeyAuthorization?.walletAddress
    ? delegatedKeyAuthorization
    : custodyWallet;

  const { walletType } = wallet;
  if (!walletType) {
    throw new Error('Wallet signer problem - try to Unlock again');
  }
  switch (walletType) {
    case 'qrSynced':
      throw new Error(
        // TODO: review text
        'QR Mobile Wallet only allows trading. To perform this action you have to use main app with your custody wallet.'
      );
    case 'delegatedKey': {
      return getDelegatedKeySigner(
        wallet,
        custodyWallet,
        await getPersonalWalletSecret(custodyWallet)
      );
    }
    case 'keystore':
    case 'privateKey':
      if (!wallet.privateKeyPersonalEncryption) {
        // All wallets should be automatically upgraded when opened updated IDEX
        throw new Error(
          'Problem upgrading wallet storage security. Please unlock your wallet again.'
        );
      }
      return getPrivateKeySigner(
        wallet.privateKeyEncrypted,
        await getPersonalWalletSecret(wallet),
        getGlobalEnforcedChain()
      );
    case 'softwareDiscovered_metaMask':
    case 'softwareDiscovered_trustWallet':
    case 'softwareDiscovered': {
      const signer = await getDiscoveredWalletSigner(
        wallet.walletDiscoveryRdns,
        true
      );
      if (networkChainCheck) {
        await throwOnIncorrectChain(signer);
      }
      const walletAddress = await signer.getAddress();
      await throwOnAddressMissmatch(wallet, signer, walletAddress);
      return signer;
    }
    case 'softwareWeb3': {
      const signer = await getSotwareWalletSigner(
        walletType,
        wallet.walletAddressOriginal
      );
      if (networkChainCheck) {
        await throwOnIncorrectChain(signer);
      }
      const web3WalletAddress = await getSoftwareWalletAccount(walletType);

      await throwOnAddressMissmatch(wallet, signer, web3WalletAddress);
      return signer;
    }
    case 'coinbaseWallet': {
      const signer = await getCoinbaseWalletSigner();
      if (networkChainCheck) {
        await throwOnIncorrectChain(signer);
      }
      await throwOnAddressMissmatch(wallet, signer);
      return signer;
    }
    case 'walletConnect': {
      const enforcedChain = getGlobalEnforcedChain();
      const signer = await getWalletConnectSigner(
        wallet.walletAddressOriginal,
        enforcedChain
      );
      // TrustWallet MultiChain Wallet always return chainId as "1"
      if (networkChainCheck) {
        await throwOnIncorrectChain(signer);
      }
      await throwOnAddressMissmatch(wallet, signer);
      return signer;
    }
    // case 'ledger':
    //   return await getLedgerSigner(wallet.hardwareWalletPath);
    // case 'trezor':
    //   return await getTrezorSigner(
    //     wallet.hardwareWalletPath,
    //     wallet.walletAddress
    //   );
    default: {
      throw new UnreachableCaseError(
        walletType,
        'Wallet not supported - contact support'
      );
    }
  }
};

/**
 * We support one-click unlock on mobile only
 * by detection of mobile wallets.
 * Metamask not yet enabled because it use same code also on desktop.
 */
export const getSupportedOneClickWallet = (
  discoveredMap: ProvidersMap,
  isMobile: boolean
): false | { type: WalletType; rdns?: string } => {
  // const web3Wallet = detectAvailableWeb3Wallet();
  // if (web3Wallet === 'trustWallet') {
  //   return 'softwareWeb3_trustWallet';
  // }
  if (isCoinbaseApp()) {
    return { type: 'coinbaseWallet' };
  }
  const discoveredList = Object.values(discoveredMap);
  const singleDiscovered = discoveredList.length === 1;
  if (!isMobile) {
    // For now, only enable auto-unlock on mobile
    return false;
  }
  if (!singleDiscovered) {
    // Auto-unlock enabled only if exactly 1 wallet available
    return false;
  }
  const rdns = discoveredList[0].info.rdns;
  const foundWallet = walletsList.find(
    wallet => wallet.rdns?.some(configRdns => configRdns === rdns)
  );

  if (!foundWallet) {
    // Generic (unknown) discovered wallets
    return { type: 'softwareDiscovered', rdns };
  }

  // Specific wallets like fixed MetaMask & TrustWallet
  return { type: foundWallet.wallet, rdns };
};

export const disconnectWallet = (walletType: WalletType) => {
  switch (walletType) {
    case 'coinbaseWallet':
      disconnectCoinbaseWallet();
      break;
    case 'walletConnect':
      removeWalletConnectFromLocalStorage();
  }
};
